import {
  AccessRuleChangePayload,
  AccessRuleChangeSet,
  AccessRuleEntitlementContext,
  AccessRuleMultiEntitlementContext,
  BlackoutDateRangeAccessRulePayload,
  BlackoutDayOfWeekAccessRulePayload,
  EffectiveDateRangeAccessRulePayload,
  MaxDaysAccessRulePayload,
  PersistedBlackoutDateRangeAccessRulePayload,
  PersistedBlackoutDayOfWeekAccessRulePayload,
  PersistedEffectiveDateRangeAccessRulePayload,
  PersistedMaxDaysAccessRulePayload,
} from "../models";
import { getRequestUtcDateTime, mapIfAny } from "./accessRuleRequestMapper";
import {
  mapEffectiveDateRange,
  mapEffectiveDateRangeUpdate,
  mapMaxDays,
  mapMaxDaysUpdate,
  mapBlackoutDateRange,
  mapBlackoutDateRangeUpdate,
  mapBlackoutDayOfWeek,
  mapBlackoutDayOfWeekUpdate,
  mapDeletedIds,
} from "./accessRuleRuleMappers";

export type AccessRuleChangeRequest = {
  Context: AccessRuleEntitlementContext;
  OperatorId: string;
  UserId: string;
  RequestUtcDateTime: string;
  EffectiveDateRangeRules: AccessRuleChangePayload<
    EffectiveDateRangeAccessRulePayload,
    PersistedEffectiveDateRangeAccessRulePayload,
    number
  >;
  MaxDaysRules: AccessRuleChangePayload<
    MaxDaysAccessRulePayload,
    PersistedMaxDaysAccessRulePayload,
    number
  >;
  BlackoutDateRangeRules: AccessRuleChangePayload<
    BlackoutDateRangeAccessRulePayload,
    PersistedBlackoutDateRangeAccessRulePayload,
    number
  >;
  BlackoutDayOfWeekRules: AccessRuleChangePayload<
    BlackoutDayOfWeekAccessRulePayload,
    PersistedBlackoutDayOfWeekAccessRulePayload,
    number
  >;
};

export type AccessRuleMultiAddRequest = {
  Contexts: AccessRuleMultiEntitlementContext[];
  OperatorId: string;
  UserId: string;
  RequestUtcDateTime: string;
  EffectiveDateRangeRules: EffectiveDateRangeAccessRulePayload[];
  MaxDaysRules: MaxDaysAccessRulePayload[];
  BlackoutDateRangeRules: BlackoutDateRangeAccessRulePayload[];
  BlackoutDayOfWeekRules: BlackoutDayOfWeekAccessRulePayload[];
};

export type AccessRuleMultiContextDeleteRequest = {
  Contexts: AccessRuleEntitlementContext[];
  OperatorId: string;
  UserId: string;
  RequestUtcDateTime: string;
};

export function buildSingleAccessRuleRequest({
  userId,
  operatorId,
  context,
  changeSet,
}: {
  userId: string;
  operatorId: string;
  context: any;
  changeSet: AccessRuleChangeSet;
}): AccessRuleChangeRequest {
  return {
    RequestUtcDateTime: getRequestUtcDateTime(),
    UserId: userId,
    OperatorId: operatorId,
    Context: context,
    ...buildRules(changeSet),
  };
}

export function buildMultiAddAccessRuleRequest({
  userId,
  operatorId,
  contexts,
  changeSet,
}: {
  userId: string;
  operatorId: string;
  contexts: any[];
  changeSet: AccessRuleChangeSet;
}): AccessRuleMultiAddRequest {
  return {
    RequestUtcDateTime: getRequestUtcDateTime(),
    UserId: userId,
    OperatorId: operatorId,
    Contexts: contexts,
    ...buildAddRules(changeSet),
  };
}

/**
 * `requestUtcDateTime` is stamped once per run and passed in, not generated here:
 * with UserId it is what ties a run's chunks together in the audit log. Generating
 * it per chunk gives every chunk its own timestamp and breaks that.
 */
export function buildMultiContextDeleteRequest({
  userId,
  operatorId,
  contexts,
  requestUtcDateTime,
}: {
  userId: string;
  operatorId: string;
  contexts: any;
  requestUtcDateTime: string;
}): AccessRuleMultiContextDeleteRequest {
  return {
    RequestUtcDateTime: requestUtcDateTime,
    UserId: userId,
    OperatorId: operatorId,
    Contexts: contexts,
  };
}

function buildRules(changeSet: AccessRuleChangeSet) {
  return {
    EffectiveDateRangeRules: {
      add: mapIfAny(
        changeSet.effectiveDateRange.created,
        mapEffectiveDateRange,
      ),
      update: mapIfAny(
        changeSet.effectiveDateRange.updated,
        mapEffectiveDateRangeUpdate,
      ),
      delete: mapDeletedIds(changeSet.effectiveDateRange.deleted),
    },
    MaxDaysRules: {
      add: mapIfAny(changeSet.maxDays.created, mapMaxDays),
      update: mapIfAny(changeSet.maxDays.updated, mapMaxDaysUpdate),
      delete: mapDeletedIds(changeSet.maxDays.deleted),
    },
    BlackoutDateRangeRules: {
      add: mapIfAny(changeSet.blackoutDateRange.created, mapBlackoutDateRange),
      update: mapIfAny(
        changeSet.blackoutDateRange.updated,
        mapBlackoutDateRangeUpdate,
      ),
      delete: mapDeletedIds(changeSet.blackoutDateRange.deleted),
    },
    BlackoutDayOfWeekRules: {
      add: mapIfAny(changeSet.blackoutDayOfWeek.created, mapBlackoutDayOfWeek),
      update: mapIfAny(
        changeSet.blackoutDayOfWeek.updated,
        mapBlackoutDayOfWeekUpdate,
      ),
      delete: mapDeletedIds(changeSet.blackoutDayOfWeek.deleted),
    },
  };
}

function buildAddRules(changeSet: AccessRuleChangeSet) {
  return {
    EffectiveDateRangeRules: (changeSet.effectiveDateRange.created ?? []).map(
      mapEffectiveDateRange,
    ),

    MaxDaysRules: (changeSet.maxDays.created ?? []).map(mapMaxDays),

    BlackoutDateRangeRules: (changeSet.blackoutDateRange.created ?? []).map(
      mapBlackoutDateRange,
    ),

    BlackoutDayOfWeekRules: (changeSet.blackoutDayOfWeek.created ?? []).map(
      mapBlackoutDayOfWeek,
    ),
  };
}
